import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { DowntimeAlert } from '@src/components/atoms/downtime-alert';
import { SEO } from '@src/components/common/seo';
import { LinkTripAction } from '@src/components/molecules/link-trip-action';
import { NoTripsHeader } from '@src/components/organisms/headers/no-trips-header-v2';
import { PastCanceledTrips } from '@src/components/pages/my-trips/components/past-canceled-trips/past-canceled-trips';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { useToast } from '@src/contexts/toast-context';
import { useProfile } from '@src/features/profile/contexts/profile-context';
import { TripListItem } from '@src/types/api/models';

import { UpcomingTripsHome } from './components/upcoming-trips-home';
import { MyTripsProps } from './types';
import { useGuestConsentConfirmationErrorToast } from './use-guest-consent-confirmation-error-toast';

import styles from './my-trips.module.css';

const AppPromotionModal = dynamic(() => import('@src/features/app-promotion-modal/app-promotion-modal'), {
  ssr: false,
});

const MyTrips = ({ trips, currentTrip }: MyTripsProps) => {
  const content = useCmsContent();
  const router = useRouter();
  const { addToast } = useToast();

  const upcomingTrips: TripListItem[] = currentTrip ? [currentTrip] : trips;
  const hasUpcomingTrips = !!upcomingTrips.length;

  const { data: profile } = useProfile();
  const isAuthenticated = !!profile;

  useGuestConsentConfirmationErrorToast();

  useEffect(() => {
    if (router.query.error) {
      const errorMessageMap = {
        'checkout-no-current-trip': content.checkoutNoCurrentTripMessage,
      };
      void router.replace({ pathname: '/trips', query: {} }, undefined, { shallow: true });
      const errorMessage = errorMessageMap[router.query.error as keyof typeof errorMessageMap];
      addToast({
        type: 'error',
        content: errorMessage,
      });
    }
  }, [addToast, content, router, router.query]);

  return (
    <>
      <section>
        <DowntimeAlert />
        <SEO title={content.navigationTrips} />
        {hasUpcomingTrips ? (
          <div className={styles.upcomingTripsWrapper} data-only-one-trip={upcomingTrips.length === 1}>
            <div className={styles.contentWrapper}>
              <UpcomingTripsHome upcomingTrips={upcomingTrips} />
              {isAuthenticated && <LinkTripAction />}
            </div>
          </div>
        ) : (
          <NoTripsHeader />
        )}

        <PastCanceledTrips />
      </section>

      <AppPromotionModal />
    </>
  );
};
export default MyTrips;
